import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Divider,
  Alert,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle as IncludedIcon,
  AttachMoney as PayPerUseIcon,
  AccessTime as HourlyLimitIcon,
  EventRepeat as UsageLimitIcon,
  MonetizationOn as HybridIcon,
  Business as FacilityIcon,
  Pool as PoolIcon,
  FitnessCenter as GymIcon,
  SportsBaseball as CourtIcon,
  DirectionsRun as TrackIcon,
  Home as StudioIcon,
  Landscape as FieldIcon,
  MeetingRoom as RoomIcon
} from '@mui/icons-material';

const getAccessTypeConfig = (t) => ({
  included: {
    icon: IncludedIcon,
    label: t('facilities:facilityAccess.accessTypes.included.label'),
    color: 'success',
    description: t('facilities:facilityAccess.accessTypes.included.description')
  },
  'pay-per-use': {
    icon: PayPerUseIcon,
    label: t('facilities:facilityAccess.accessTypes.payPerUse.label'),
    color: 'warning',
    description: t('facilities:facilityAccess.accessTypes.payPerUse.description')
  },
  'hourly-limit': {
    icon: HourlyLimitIcon,
    label: t('facilities:facilityAccess.accessTypes.hourlyLimit.label'),
    color: 'info',
    description: t('facilities:facilityAccess.accessTypes.hourlyLimit.description')
  },
  'usage-limit': {
    icon: UsageLimitIcon,
    label: t('facilities:facilityAccess.accessTypes.usageLimit.label'),
    color: 'secondary',
    description: t('facilities:facilityAccess.accessTypes.usageLimit.description')
  },
  hybrid: {
    icon: HybridIcon,
    label: t('facilities:facilityAccess.accessTypes.hybrid.label'),
    color: 'primary',
    description: t('facilities:facilityAccess.accessTypes.hybrid.description')
  }
});

const FACILITY_ICONS = {
  gym: GymIcon,
  pool: PoolIcon,
  court: CourtIcon,
  studio: StudioIcon,
  track: TrackIcon,
  field: FieldIcon,
  room: RoomIcon
};

const FacilityAccessDisplay = ({
  facilityAccess = [],
  memberType = null,
  showDetailed = false,
  compact = false
}) => {
  const { t } = useTranslation();
  const formatPrice = (amount, currency = 'TRY') => {
    if (amount === 0) return t('facilities:facilityAccess.pricing.free');
    return `${amount} ${currency}`;
  };

  const getFacilityIcon = (facilityType) => {
    const IconComponent = FACILITY_ICONS[facilityType] || FacilityIcon;
    return IconComponent;
  };

  const getAccessTypeInfo = (accessType) => {
    const config = getAccessTypeConfig(t);
    return config[accessType] || config.included;
  };

  const formatLimits = (limits) => {
    const limitStrings = [];
    
    if (limits.hoursPerDay) limitStrings.push(`${limits.hoursPerDay}h/day`);
    if (limits.hoursPerWeek) limitStrings.push(`${limits.hoursPerWeek}h/week`);
    if (limits.hoursPerMonth) limitStrings.push(`${limits.hoursPerMonth}h/month`);
    if (limits.usesPerDay) limitStrings.push(`${limits.usesPerDay} uses/day`);
    if (limits.usesPerWeek) limitStrings.push(`${limits.usesPerWeek} uses/week`);
    if (limits.usesPerMonth) limitStrings.push(`${limits.usesPerMonth} uses/month`);
    
    return limitStrings.join(', ') || t('facilities:facilityAccess.pricing.noLimits');
  };

  const renderPricingInfo = (access) => {
    const { accessType, pricing } = access;
    
    switch (accessType) {
      case 'included':
        return <Typography variant="body2" color="success.main">{t('facilities:facilityAccess.includedInPlan')}</Typography>;
        
      case 'pay-per-use':
        return (
          <Box>
            <Typography variant="body2" color="warning.main">
              {formatPrice(pricing.pricePerUse, pricing.currency)} {t('facilities:facilityAccess.pricing.perUse')}
            </Typography>
          </Box>
        );
        
      case 'hourly-limit':
        return (
          <Box>
            <Typography variant="body2" color="info.main">
              {t('facilities:facilityAccess.pricing.limitedHoursIncluded')}
            </Typography>
            {pricing.pricePerHour > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('facilities:facilityAccess.pricing.overage')}: {formatPrice(pricing.pricePerHour, pricing.currency)}{t('facilities:facilityAccess.pricing.perHour')}
              </Typography>
            )}
          </Box>
        );
        
      case 'usage-limit':
        return (
          <Box>
            <Typography variant="body2" color="secondary.main">
              {t('facilities:facilityAccess.pricing.limitedUsesIncluded')}
            </Typography>
            {pricing.pricePerUse > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('facilities:facilityAccess.pricing.extraUse')}: {formatPrice(pricing.pricePerUse, pricing.currency)}
              </Typography>
            )}
          </Box>
        );
        
      case 'hybrid':
        return (
          <Box>
            <Typography variant="body2" color="primary.main">
              {formatPrice(pricing.pricePerUse, pricing.currency)} {t('facilities:facilityAccess.pricing.baseFeePlus')} {formatPrice(pricing.pricePerHour, pricing.currency)}{t('facilities:facilityAccess.pricing.perHour')}
            </Typography>
          </Box>
        );
        
      default:
        return null;
    }
  };

  const renderTimeRestrictions = (restrictions) => {
    if (!restrictions.allowedTimeSlots?.length && !restrictions.blockedDays?.length) {
      return null;
    }

    return (
      <Box sx={{ mt: 1 }}>
        {restrictions.allowedTimeSlots?.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block">
            Available: {restrictions.allowedTimeSlots.map(slot => 
              `${slot.startTime}-${slot.endTime} (${slot.daysOfWeek?.join(', ') || 'All days'})`
            ).join(', ')}
          </Typography>
        )}
        {restrictions.blockedDays?.length > 0 && (
          <Typography variant="caption" color="error.main" display="block">
            Blocked: {restrictions.blockedDays.join(', ')}
          </Typography>
        )}
        {restrictions.requiresApproval && (
          <Typography variant="caption" color="warning.main" display="block">
            Requires admin approval
          </Typography>
        )}
      </Box>
    );
  };

  if (!facilityAccess || facilityAccess.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        {t('facilities:facilityAccess.noAccessConfigured')}
      </Alert>
    );
  }

  if (compact) {
    // Compact view for plan cards
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('facilities:facilityAccess.titleWithCount', { count: facilityAccess.length })}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {facilityAccess.slice(0, 3).map((access, index) => {
            const typeInfo = getAccessTypeInfo(access.accessType);
            const FacilityIconComponent = getFacilityIcon(access.facility?.type);
            
            return (
              <Tooltip 
                key={index}
                title={`${access.facility?.name}: ${typeInfo.description}`}
                arrow
              >
                <Chip
                  icon={<FacilityIconComponent />}
                  label={access.facility?.name}
                  size="small"
                  color={typeInfo.color}
                  variant={access.accessType === 'included' ? 'filled' : 'outlined'}
                />
              </Tooltip>
            );
          })}
          {facilityAccess.length > 3 && (
            <Chip
              label={`+${facilityAccess.length - 3} more`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Box>
    );
  }

  // Detailed view for plan details page
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('facilities:facilityAccess.title')}
      </Typography>
      
      <Grid container spacing={2}>
        {facilityAccess.map((access, index) => {
          const typeInfo = getAccessTypeInfo(access.accessType);
          const FacilityIconComponent = getFacilityIcon(access.facility?.type);
          const IconComponent = typeInfo.icon;

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FacilityIconComponent sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="div">
                      {access.facility?.name}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Tooltip title={typeInfo.description}>
                      <Chip
                        icon={<IconComponent />}
                        label={typeInfo.label}
                        color={typeInfo.color}
                        size="small"
                        variant={access.accessType === 'included' ? 'filled' : 'outlined'}
                      />
                    </Tooltip>
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {access.facility?.type?.charAt(0).toUpperCase() + access.facility?.type?.slice(1)} •
                    Capacity: {access.facility?.capacity || 'N/A'}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {renderPricingInfo(access)}

                  {showDetailed && (
                    <>
                      {access.limits && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t('facilities:facilityAccess.usageLimits')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatLimits(access.limits)}
                          </Typography>
                          {access.limits.maxAdvanceBookingDays && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {t('facilities:facilityAccess.bookAdvanceDays', { days: access.limits.maxAdvanceBookingDays })}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {renderTimeRestrictions(access.restrictions)}

                      {access.notes && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {access.notes}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {showDetailed && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            💡 <strong>{t('facilities:facilityAccess.tip.title')}</strong> {t('facilities:facilityAccess.tip.description')}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default FacilityAccessDisplay;